import React, { forwardRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { resolveFaIcon } from 'helpers/faIcons';

const variantClasses = {
  primary: 'bg-primaryColor text-white hover:bg-primaryColor/90 border border-transparent',
  secondary: 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200',
  danger: 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200',
  success: 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200',
  warning: 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200',
  light: 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200',
  info: 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200',
  none: 'bg-transparent text-slate-500 hover:bg-slate-100 border border-transparent',
  'outline-primary':
    'bg-transparent text-primaryColor hover:bg-primaryColor/10 border border-primaryColor',
};

const baseClasses =
  'inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40 disabled:opacity-50 disabled:cursor-not-allowed';

// Map legacy SCSS color utility classes to Tailwind so consumers don't need App.scss.
const legacyColorMap = {
  'c-primary': 'text-primaryColor hover:text-primaryColor/80',
  'c-danger': 'text-red-500 hover:text-red-600',
  'c-warning': 'text-amber-500 hover:text-amber-600',
};

const TableActionButton = forwardRef(function TableActionButton(
  {
    variant,
    icon,
    label,
    title,
    ariaLabel,
    className = '',
    style,
    onClick,
    'data-testid': dataTestId,
    disabled,
  },
  ref,
) {
  // Detect and strip legacy SCSS color classes, replace with Tailwind equivalents.
  let colorOverride = '';
  let cleanClassName = className;
  for (const [legacyCls, tailwindCls] of Object.entries(legacyColorMap)) {
    if (cleanClassName.includes(legacyCls)) {
      cleanClassName = cleanClassName.replace(legacyCls, '').trim();
      colorOverride = tailwindCls;
    }
  }

  const variantCls = variantClasses[variant] ?? variantClasses.none;
  // colorOverride replaces the text/hover-text portion of the variant when present.
  const textCls = colorOverride || '';

  // Guarded lookup — a broken icon getter must not crash the row (see faIcons.js).
  const faIcon = resolveFaIcon(icon);

  // Icon-only row actions need an accessible name + a hover tooltip. Fall back
  // through ariaLabel → title → label so callers can pass whichever they have.
  const accessibleName = ariaLabel || title || label;

  return (
    <button
      ref={ref}
      type="button"
      className={`${baseClasses} ${variantCls} ${textCls} ${cleanClassName}`.trim()}
      style={style}
      onClick={onClick}
      disabled={disabled}
      data-testid={dataTestId}
      title={title || accessibleName}
      aria-label={accessibleName}
    >
      {faIcon && <FontAwesomeIcon icon={faIcon} />}
      {label && <span>{label}</span>}
    </button>
  );
});

export default TableActionButton;
